import { useEffect, useState, type FormEvent } from "react";
import {
  BellRing,
  CheckCircle2,
  Download,
  Globe2,
  KeyRound,
  Mail,
  Smartphone,
  UserRound,
} from "lucide-react";
import { useNavigate } from "../../app/router";
import { clearToken, readToken, tenantTokenKey } from "../../app/session";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";
import { useTranslation, type AppLocale } from "../../i18n/i18n";
import { ApiError, apiRequest } from "../../lib/api";
import { useTenantApp } from "../app/TenantAppContext";

function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }
  return output;
}

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function SettingsPage() {
  const { locale, setLocale, t } = useTranslation();
  const navigate = useNavigate();
  const { updateCurrentUser } = useTenantApp();
  const [preferences, setPreferences] = useState({
    inAppEnabled: true,
    pushEnabled: true,
    emailEnabled: false,
  });
  const [pushConfig, setPushConfig] = useState<{
    enabled: boolean;
    publicKey: string | null;
  }>({
    enabled: false,
    publicKey: null,
  });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [pushActive, setPushActive] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(
    null,
  );
  const [installed, setInstalled] = useState(
    window.matchMedia("(display-mode: standalone)").matches,
  );
  const [profile, setProfile] = useState({
    employeeCode: "",
    email: "",
    username: "",
    firstName: "",
    lastName: "",
    phone: "",
    jobTitle: "" as string | null,
    locale: "hr-HR" as AppLocale,
  });
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmation: "",
  });
  const [accountBusy, setAccountBusy] = useState(false);

  useEffect(() => {
    const token = readToken(tenantTokenKey);
    Promise.all([
      apiRequest<typeof preferences>("/api/v1/notifications/preferences", {
        accessToken: token,
      }),
      apiRequest<typeof pushConfig>("/api/v1/notifications/push/config", {
        accessToken: token,
      }),
      apiRequest<typeof profile>("/api/v1/tenant/profile", {
        accessToken: token,
      }),
    ])
      .then(([preferenceResponse, configResponse, profileResponse]) => {
        setPreferences(preferenceResponse);
        setPushConfig(configResponse);
        setProfile(profileResponse);
        if ("serviceWorker" in navigator) {
          void navigator.serviceWorker.ready
            .then((registration) => registration.pushManager.getSubscription())
            .then((subscription) => setPushActive(Boolean(subscription)));
        }
      })
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Postavke nisu dostupne.",
        ),
      );
    const beforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const appInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", appInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", appInstalled);
    };
  }, []);

  async function savePreferences(next = preferences) {
    setPreferences(next);
    setMessage("");
    await apiRequest("/api/v1/notifications/preferences", {
      method: "PUT",
      accessToken: readToken(tenantTokenKey),
      body: JSON.stringify(next),
    });
    setMessage("Postavke su spremljene.");
  }

  async function changeLocale(nextLocale: AppLocale) {
    setLocale(nextLocale);
    await apiRequest("/api/v1/tenant/preferences/locale", {
      method: "PATCH",
      accessToken: readToken(tenantTokenKey),
      body: JSON.stringify({ locale: nextLocale }),
    });
    setMessage("Jezik je spremljen na vaš korisnički račun.");
  }

  async function enablePush() {
    setError("");
    setMessage("");
    if (!pushConfig.enabled || !pushConfig.publicKey) {
      setError("Push ključevi još nisu postavljeni u .env datoteci servera.");
      return;
    }
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setError("Ovaj preglednik ne podržava PWA push obavijesti.");
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      setError("Dozvola za obavijesti nije odobrena.");
      return;
    }
    const registration = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(pushConfig.publicKey),
      }));
    await apiRequest("/api/v1/notifications/push/subscriptions", {
      method: "POST",
      accessToken: readToken(tenantTokenKey),
      body: JSON.stringify(subscription.toJSON()),
    });
    await savePreferences({ ...preferences, pushEnabled: true });
    setPushActive(true);
    setMessage("Push obavijesti su uključene na ovom uređaju.");
  }

  async function installApp() {
    if (!installPrompt) {
      setMessage(
        "Za instalaciju otvorite izbornik preglednika i odaberite „Instaliraj aplikaciju“ ili „Dodaj na početni zaslon“.",
      );
      return;
    }
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setInstalled(true);
      setMessage("Šihterica je instalirana na ovom uređaju.");
    }
    setInstallPrompt(null);
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setAccountBusy(true);
    setError("");
    setMessage("");
    try {
      await apiRequest("/api/v1/tenant/profile", {
        method: "PATCH",
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({
          firstName: profile.firstName,
          lastName: profile.lastName,
          username: profile.username,
          phone: profile.phone,
          locale: profile.locale,
        }),
      });
      setLocale(profile.locale);
      updateCurrentUser({
        firstName: profile.firstName,
        lastName: profile.lastName,
        locale: profile.locale,
      });
      setMessage("Profil je spremljen.");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Profil nije moguće spremiti.");
    } finally {
      setAccountBusy(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (passwords.newPassword !== passwords.confirmation) {
      setError("Nove lozinke se ne podudaraju.");
      return;
    }
    setAccountBusy(true);
    try {
      await apiRequest("/api/v1/tenant/password", {
        method: "POST",
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword,
        }),
      });
      clearToken(tenantTokenKey);
      navigate("/login?passwordChanged=1", { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Lozinku nije moguće promijeniti.");
      setAccountBusy(false);
    }
  }

  return (
    <div className="module-page">
      <header className="module-page__hero">
        <div>
          <span className="eyebrow">OSOBNO · SINKRONIZIRANO</span>
          <h1>{t("settings.title")}</h1>
          <p>{t("settings.subtitle")}</p>
        </div>
      </header>
      {error && <div className="form-error">{error}</div>}
      {message && <div className="form-success">{message}</div>}
      <section className="settings-grid">
        <form className="settings-card settings-card--account" onSubmit={saveProfile}>
          <span className="settings-card__icon"><UserRound /></span>
          <div className="settings-card__heading">
            <h2>Moj profil</h2>
            <p>{profile.employeeCode}{profile.jobTitle ? ` · ${profile.jobTitle}` : ""} · {profile.email}</p>
          </div>
          <div className="settings-account-form">
            <label className="field"><span>Ime</span><input required value={profile.firstName} onChange={(event) => setProfile({ ...profile, firstName: event.target.value })} /></label>
            <label className="field"><span>Prezime</span><input required value={profile.lastName} onChange={(event) => setProfile({ ...profile, lastName: event.target.value })} /></label>
            <label className="field"><span>Korisničko ime</span><input value={profile.username} onChange={(event) => setProfile({ ...profile, username: event.target.value })} placeholder="npr. luka.b" /></label>
            <label className="field"><span>Telefon</span><input value={profile.phone} onChange={(event) => setProfile({ ...profile, phone: event.target.value })} /></label>
            <label className="field"><span>Jezik</span><select value={profile.locale} onChange={(event) => setProfile({ ...profile, locale: event.target.value as AppLocale })}><option value="hr-HR">Hrvatski</option><option value="en-US">English</option><option value="de-DE">Deutsch</option></select></label>
            <button className="button button--primary" disabled={accountBusy}>Spremi profil</button>
          </div>
        </form>
        <form className="settings-card settings-card--account" onSubmit={changePassword}>
          <span className="settings-card__icon"><KeyRound /></span>
          <div className="settings-card__heading">
            <h2>Promjena lozinke</h2>
            <p>Nakon promjene odjavljuju se sve aktivne sesije.</p>
          </div>
          <div className="settings-account-form">
            <label className="field"><span>Trenutačna lozinka</span><input type="password" autoComplete="current-password" minLength={8} required value={passwords.currentPassword} onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })} /></label>
            <label className="field"><span>Nova lozinka</span><input type="password" autoComplete="new-password" minLength={12} required value={passwords.newPassword} onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })} /></label>
            <label className="field"><span>Ponovite novu lozinku</span><input type="password" autoComplete="new-password" minLength={12} required value={passwords.confirmation} onChange={(event) => setPasswords({ ...passwords, confirmation: event.target.value })} /></label>
            <button className="button button--primary" disabled={accountBusy}>Promijeni lozinku</button>
          </div>
        </form>
        <article className="settings-card settings-card--install">
          <span className="settings-card__icon">
            <Download />
          </span>
          <div>
            <h2>Instaliraj Šihtericu</h2>
            <p>
              Puna PWA aplikacija na početnom zaslonu mobitela, tableta ili
              računala.
            </p>
          </div>
          {installed ? (
            <span className="settings-state settings-state--success">
              <CheckCircle2 /> Instalirano
            </span>
          ) : (
            <button
              className="button button--primary"
              onClick={() => void installApp()}
            >
              Instaliraj aplikaciju
            </button>
          )}
        </article>
        <article className="settings-card">
          <span className="settings-card__icon">
            <Globe2 />
          </span>
          <div>
            <h2>{t("settings.language")}</h2>
            <p>Odabir se sprema po prijavljenom korisniku.</p>
          </div>
          <LanguageSwitcher
            value={locale}
            onChange={changeLocale}
            ariaLabel={t("settings.language")}
            variant="settings"
          />
        </article>
        <article className="settings-card">
          <span className="settings-card__icon">
            <BellRing />
          </span>
          <div>
            <h2>{t("settings.inApp")}</h2>
            <p>Obavijesti ostaju u centru aplikacije.</p>
          </div>
          <button
            className={`switch ${preferences.inAppEnabled ? "is-on" : ""}`}
            onClick={() =>
              void savePreferences({
                ...preferences,
                inAppEnabled: !preferences.inAppEnabled,
              })
            }
          >
            <i />
          </button>
        </article>
        <article className="settings-card">
          <span className="settings-card__icon">
            <Mail />
          </span>
          <div>
            <h2>{t("settings.email")}</h2>
            <p>Dodjele zadataka i odluke stižu na e-mail.</p>
          </div>
          <button
            className={`switch ${preferences.emailEnabled ? "is-on" : ""}`}
            onClick={() =>
              void savePreferences({
                ...preferences,
                emailEnabled: !preferences.emailEnabled,
              })
            }
          >
            <i />
          </button>
        </article>
        <article className="settings-card settings-card--push">
          <span className="settings-card__icon">
            <Smartphone />
          </span>
          <div>
            <h2>{t("settings.push")}</h2>
            <p>
              Trenutačne obavijesti na mobitelu i kada aplikacija nije otvorena.
            </p>
          </div>
          {pushActive ? (
            <span className="settings-state settings-state--success">
              <CheckCircle2 /> Uključene
            </span>
          ) : (
            <button
              className="button button--primary"
              onClick={() => void enablePush()}
            >
              {t("settings.enablePush")}
            </button>
          )}
        </article>
      </section>
    </div>
  );
}
